import { useState } from 'react';
import { Button, StateBox, TierBadge, humanize } from '@garnet/ui';
import type { TriageItem } from '../../data/types.js';
import type { Route } from '../../nav.js';
import { AdvisorMark, Citations, Confidence } from '../../components/advisor.js';

/* Garnet · AI Advisor — Triage / next-best-action.
   The Advisor ranks open flags by likely materiality and proposes the next
   step for each, shown as recommendations the operator chooses among. */

type Phase = 'pending' | 'accepted' | 'rejected';

function MaterialityBadge({ m, label }: { m: number; label: string }) {
  return (
    <span className="tri-mat" data-m={m} title={'likely materiality · ' + label}>
      <span className="mv">
        <i />
        <i />
        <i />
        <i />
      </span>
      materiality · {label}
    </span>
  );
}

function TriageCard({ t, go }: { t: TriageItem; go: (r: Route) => void }) {
  const [phase, setPhase] = useState<Phase>('pending');

  return (
    <div className="adv-card" style={phase !== 'pending' ? { background: 'var(--panel)', borderLeftColor: 'var(--rule)' } : undefined}>
      <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <span className="tri-rank">{t.rank}</span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="ac-head">
            <div style={{ minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <span style={{ fontFamily: 'var(--display)', fontWeight: 600, fontSize: 14.5, color: 'var(--ink)' }}>
                  {t.flag} {t.supplier}
                </span>
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>
                  {t.record} · {t.case}
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 7, flexWrap: 'wrap' }}>
                <MaterialityBadge m={t.materiality} label={t.materialityLabel} />
                <TierBadge level={t.tier} sm />
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 10.5,
                    color: t.sla.includes('overdue') ? 'var(--flag)' : 'var(--muted)',
                    fontWeight: 600,
                  }}
                >
                  SLA {t.sla}
                </span>
                <span
                  style={{
                    fontFamily: 'var(--mono)',
                    fontSize: 10.5,
                    color: 'var(--muted)',
                    background: 'var(--rule-soft)',
                    border: '1px solid var(--rule)',
                    borderRadius: 2,
                    padding: '1px 6px',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {humanize(t.flagType)}
                </span>
              </div>
            </div>
            <Confidence level={t.confidence} />
          </div>

          {phase === 'pending' ? (
            <>
              <p className="ac-rationale" style={{ marginTop: 11 }}>
                {t.why}
              </p>
              <div style={{ marginTop: 11 }}>
                <Citations cites={t.cites} />
              </div>
              <div className="ac-proposed" style={{ marginTop: 12 }}>
                <span className="pl">you act</span>
                <span className="pt">{t.proposed}</span>
              </div>
              <div className="ac-actions">
                <Button variant="primary" sm icon="check" onClick={() => setPhase('accepted')}>
                  {t.perform}
                </Button>
                <Button variant="secondary" sm icon="exchange" onClick={() => setPhase('accepted')}>
                  Modify
                </Button>
                <Button variant="ghost" sm icon="x" onClick={() => setPhase('rejected')}>
                  Defer
                </Button>
                <Button variant="ghost" sm icon="arrow" onClick={() => go('cases')}>
                  Open case
                </Button>
              </div>
            </>
          ) : (
            <div className="adv-resolved-bar" style={{ marginTop: 12 }}>
              <span className={'rb-tag ' + (phase === 'accepted' ? 'accepted' : 'rejected')}>
                {phase === 'accepted' ? 'actioned' : 'deferred'}
              </span>
              <span className="rb-body">
                <span className="rb-who">
                  {phase === 'accepted'
                    ? 'You · just now — next step raised. The flag stays open until the engine and an approver clear it.'
                    : 'You · just now — deferred. It returns to the queue at its ranked position.'}
                </span>{' '}
                <span className="rb-link" onClick={() => setPhase('pending')}>
                  undo
                </span>
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export function Triage({ triage, go }: { triage: TriageItem[]; go: (r: Route) => void }) {
  return (
    <div className="adv-stage-inner">
      <div className="adv-h">
        <div className="eb">§ Triage · advisor</div>
        <h1>Next best action</h1>
        <p>
          Across the open flags, the Advisor ranks by likely materiality — tier, safety consequence, SLA, units affected —
          and proposes the next step for each. It orders the queue; it does not work it. You choose what to action.
        </p>
      </div>

      <div className="adv-principle">
        <span className="ap-mark">
          <AdvisorMark size={14} />
        </span>
        <span>
          Ranking is a <b>recommendation</b>, not a verdict. Materiality is the Advisor's estimate of consequence; clearing
          any flag still takes the engine and an authorised human.
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 14, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 10.5, letterSpacing: '.08em', textTransform: 'uppercase', color: 'var(--muted)' }}>
          {triage.length} open flags · ranked highest materiality first
        </span>
        <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--muted)' }}>engine 2026.05.2 · materiality model</span>
      </div>

      {triage.length === 0 ? (
        <StateBox kind="empty" title="No open flags to triage" />
      ) : (
        <div style={{ display: 'grid', gap: 12 }}>
          {triage.map((t) => (
            <TriageCard key={t.ref} t={t} go={go} />
          ))}
        </div>
      )}
    </div>
  );
}
